import {Button, Grid, IconButton, Typography, TextField} from "@mui/material"
import React from "react"
import {styled} from "@mui/material/styles"
import CloseIcon from "@mui/icons-material/Close"
import {DatePicker} from "@mui/x-date-pickers/DatePicker"
import {LocalizationProvider, PickersLayout} from "@mui/x-date-pickers"
import {AdapterDateFns} from "@mui/x-date-pickers/AdapterDateFns"
// @ts-expect-error - date-fns import needs dependency/build system upgrade
import format from "date-fns/format"

import {SettlementOfferResponse} from "@indebted/api"

interface OfferModalFormProps {
	onCancel: () => void
	onCalculate: (date: string, percentage: string | undefined) => void
	offerData: SettlementOfferResponse
	error?: string
	storedData?: {date: string}
}

function OfferModalForm({onCancel, onCalculate, offerData, error, storedData}: OfferModalFormProps) {
	const [percentage, setPercentage] = React.useState<string | undefined>()
	const [date, setDate] = React.useState<Date>(new Date())

	React.useEffect(() => {
		setDate(new Date(storedData?.date ?? offerData.DefaultEndDate))
	}, [offerData.DefaultEndDate, storedData?.date])

	const onChangePercentage = (e: React.ChangeEvent<HTMLInputElement>) => {
		e.preventDefault()
		const numValue = parseFloat(e.target.value)
		if (numValue < offerData.MinPercentage) e.target.value = offerData.MinPercentage.toString()
		if (numValue > offerData.MaxPercentage) e.target.value = offerData.MaxPercentage.toString()
		setPercentage(e.target.value)
	}

	const onChangeDate = (date: Date | null) => {
		if (date) setDate(date)
	}

	return (
		<Grid container direction="column" spacing={3}>
			<Grid container direction="row" spacing={3} style={{alignItems: "center", justifyContent: "flex-start"}}>
				<Grid size={{xs: 9, sm: 10}} style={{paddingLeft: "12px", paddingTop: "18px"}}>
					<Typography
						variant="h5"
						style={{fontWeight: "bold", lineHeight: 1.3, paddingBottom: "24px"}}
						align="left"
					>
						Settle account with discount
					</Typography>
				</Grid>
				<Grid size={{xs: 3, sm: 2}} style={{display: "flex", justifyContent: "flex-end"}}>
					<IconButton aria-label="close" onClick={onCancel}>
						<CloseIcon htmlColor="rgba(50,50,50,0.45)" />
					</IconButton>
				</Grid>
			</Grid>
			{error && (
				<Grid size={12} style={{paddingLeft: "6px"}}>
					<Grid container direction="row">
						<Grid size="grow">
							<Typography variant="caption" align="left" color="error">
								{error}
							</Typography>
						</Grid>
					</Grid>
				</Grid>
			)}
			<Grid size={12} style={{paddingLeft: "6px"}}>
				<Grid container direction="row" spacing={3}>
					<Grid size={4}>
						<TextField
							label="%"
							value={percentage}
							onChange={onChangePercentage}
							variant="outlined"
							autoFocus
							fullWidth
							inputProps={{
								type: "number",
								min: offerData.MinPercentage,
								max: offerData.MaxPercentage,
								suffix: "%",
								step: "0.01",
							}}
						/>
					</Grid>
					<Grid size={8}>
						<LocalizationProvider dateAdapter={AdapterDateFns}>
							<StyledDatePicker
								slots={{
									layout: StyledPickersLayout as any,
								}}
								value={date}
								onChange={onChangeDate}
								defaultValue={date}
								format="dd MMMM yyyy"
								minDate={new Date(offerData.DefaultEndDate)}
								label="Pay until"
							/>
						</LocalizationProvider>
					</Grid>
				</Grid>
			</Grid>
			<Grid size={12} style={{paddingLeft: "6px"}}>
				<hr />
			</Grid>
			<Grid size={12} style={{paddingLeft: "6px"}}>
				<Grid container direction="row" justifyContent="space-between">
					<Grid size="grow">
						<Typography variant="subtitle1" align="left" color="textSecondary">
							Full balance:
						</Typography>
					</Grid>
					<Grid size="grow">
						<Typography variant="subtitle1" align="right" color="textSecondary">
							{offerData.FullBalance}
						</Typography>
					</Grid>
				</Grid>
			</Grid>
			<Grid size={12}>
				<Button
					fullWidth
					variant="outlined"
					type="button"
					onClick={() => onCalculate(format(date, offerData.DateFormat), percentage?.toString())}
				>
					Review discount
				</Button>
			</Grid>
		</Grid>
	)
}

const StyledDatePicker = styled(DatePicker)({
	".Mui-focused fieldset.MuiOutlinedInput-notchedOutline": {
		borderColor: "rgb(71, 76, 222)",
		color: "rgb(71, 76, 222)",
	},
	".Mui-focused.MuiInputLabel-outlined": {
		color: "rgb(71, 76, 222)",
	},
})

const StyledPickersLayout = styled(PickersLayout)({
	".MuiPickersDay-root.Mui-selected": {
		backgroundColor: "rgb(71, 76, 222)",
		"&:hover": {
			backgroundColor: "rgb(49, 53, 155)",
		},
	},
})

export {OfferModalForm}
